import React, { createContext, useContext, useState, useEffect } from 'react';

interface HelpPreferences {
  seenGuides: string[];
  notificationsEnabled: boolean;
  theme: 'light' | 'dark';
  dismissedNotifications: string[];
}

interface HelpContextType {
  isOpen: boolean;
  currentGuide: string | null;
  preferences: HelpPreferences;
  userRole: string | null;
  newGuides: string[];
  notifications: Array<{ id: string; guideId: string; title: string; message: string }>;
  openHelp: (guideId: string) => void;
  closeHelp: () => void;
  updatePreferences: (prefs: Partial<HelpPreferences>) => void;
  hasAccess: (guideId: string, role: string) => boolean;
  markGuideAsSeen: (guideId: string) => void;
  dismissNotification: (notificationId: string) => void;
  checkForNewGuides: () => void;
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

export const useHelp = () => {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};


interface HelpProviderProps {
  children: React.ReactNode;
}

const defaultPreferences: HelpPreferences = {
  seenGuides: [],
  notificationsEnabled: true,
  theme: 'light',
  dismissedNotifications: []
};

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentGuide, setCurrentGuide] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<HelpPreferences>(defaultPreferences);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [newGuides, setNewGuides] = useState<string[]>([]);
  const [notifications, setNotifications] = useState<Array<{ id: string; guideId: string; title: string; message: string }>>([]);

  useEffect(() => {
    // Load preferences from localStorage
    const storedPrefs = localStorage.getItem('help_preferences');
    if (storedPrefs) {
      try {
        setPreferences(JSON.parse(storedPrefs));
      } catch (error) {
        console.warn('Failed to parse help preferences from localStorage');
      }
    }

    // Get user role from localStorage or context
    const storedUser = localStorage.getItem('user_data');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserRole(userData.role);
      } catch (error) {
        console.warn('Failed to parse user data from localStorage');
      }
    }
  }, []);

  // Check for new guides when user role or preferences change
  useEffect(() => {
    if (userRole) {
      checkForNewGuides();
    }
  }, [userRole, preferences.seenGuides]);

  const openHelp = (guideId: string) => {
    setCurrentGuide(guideId);
    setIsOpen(true);
  };

  const closeHelp = () => {
    setIsOpen(false);
    setCurrentGuide(null);
  };

  const updatePreferences = (newPrefs: Partial<HelpPreferences>) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    localStorage.setItem('help_preferences', JSON.stringify(updated));
  };

  const markGuideAsSeen = (guideId: string) => {
    if (!preferences.seenGuides.includes(guideId)) {
      updatePreferences({
        seenGuides: [...preferences.seenGuides, guideId]
      });
    }
  };

  const hasAccess = (guideId: string, role: string): boolean => {
    // If no role provided, allow access (for cases where role is not yet determined)
    if (!role) {
      return true;
    }

    // Developer has access to everything
    if (role === 'developer') {
      return true;
    }

    // Admin roles have access to admin guides
    const isAdminRole = ['admin', 'finance_admin', 'town_admin', 'member_admin'].includes(role);
    if (guideId.includes('admin') && !isAdminRole) {
      return false;
    }

    // Developer-only guides
    if (guideId.includes('developer') && role !== 'developer') {
      return false;
    }

    // Member portal guides should be accessible to all roles
    if (guideId.includes('member-portal')) {
      return true;
    }

    // Allow access to all other guides for all roles
    return true;
  };

  const checkForNewGuides = () => {
    // Define available guides based on user role
    const availableGuides = [];
    if (userRole) {
      // Dashboard guides
      if (hasAccess('dashboard/dashboard-overview', userRole)) {
        availableGuides.push('dashboard/dashboard-overview');
      }
      if (hasAccess('dashboard/chart-usage', userRole)) {
        availableGuides.push('dashboard/chart-usage');
      }
      if (hasAccess('dashboard/filter-functionality', userRole)) {
        availableGuides.push('dashboard/filter-functionality');
      }
      if (hasAccess('dashboard/export-features', userRole)) {
        availableGuides.push('dashboard/export-features');
      }
      
      // Member guides
      if (hasAccess('members/member-list-features', userRole)) {
        availableGuides.push('members/member-list-features');
      }
      if (hasAccess('members/add-edit-members', userRole)) {
        availableGuides.push('members/add-edit-members');
      }
      if (hasAccess('members/financial-information', userRole)) {
        availableGuides.push('members/financial-information');
      }
      
      // Contribution guides
      if (hasAccess('contributions/contribution-list', userRole)) {
        availableGuides.push('contributions/contribution-list');
      }
      if (hasAccess('contributions/add-contribution', userRole)) {
        availableGuides.push('contributions/add-contribution');
      }
      
      // Expense guides
      if (hasAccess('expenses/expense-list', userRole)) {
        availableGuides.push('expenses/expense-list');
      }
      if (hasAccess('expenses/add-expense', userRole)) {
        availableGuides.push('expenses/add-expense');
      }
      
      // Event guides
      if (hasAccess('events/event-list', userRole)) {
        availableGuides.push('events/event-list');
      }
      if (hasAccess('events/add-event', userRole)) {
        availableGuides.push('events/add-event');
      }
      
      // Benefit guides
      if (hasAccess('benefits/benefit-list', userRole)) {
        availableGuides.push('benefits/benefit-list');
      }
      if (hasAccess('benefits/benefit-form', userRole)) {
        availableGuides.push('benefits/benefit-form');
      }
      
      // Town guides
      if (hasAccess('towns/town-list', userRole)) {
        availableGuides.push('towns/town-list');
      }
      if (hasAccess('towns/add-town', userRole)) {
        availableGuides.push('towns/add-town');
      }
      
      // Report guides
      if (hasAccess('reports/report-center', userRole)) {
        availableGuides.push('reports/report-center');
      }
      if (hasAccess('reports/enhanced-financial', userRole)) {
        availableGuides.push('reports/enhanced-financial');
      }
      
      // New component guides
      if (hasAccess('sms/sms-configuration', userRole)) {
        availableGuides.push('sms/sms-configuration');
      }
      if (hasAccess('email/email-templates', userRole)) {
        availableGuides.push('email/email-templates');
      }
      if (hasAccess('messages/messaging-system', userRole)) {
        availableGuides.push('messages/messaging-system');
      }
      if (hasAccess('audit/audit-trail', userRole)) {
        availableGuides.push('audit/audit-trail');
      }
      if (hasAccess('approvals/financial-approvals', userRole)) {
        availableGuides.push('approvals/financial-approvals');
      }
      if (hasAccess('settings/system-settings', userRole)) {
        availableGuides.push('settings/system-settings');
      }
      if (hasAccess('admin/brand-settings', userRole)) {
        availableGuides.push('admin/brand-settings');
      }
      if (hasAccess('stats/dashboard', userRole)) {
        availableGuides.push('stats/dashboard');
      }
      if (hasAccess('reminders/reminder-management', userRole)) {
        availableGuides.push('reminders/reminder-management');
      }
      
      // Assignment guides
      if (hasAccess('assignments/assignment-management', userRole)) {
        availableGuides.push('assignments/assignment-management');
      }
      if (hasAccess('assignments/assignment-generation', userRole)) {
        availableGuides.push('assignments/assignment-generation');
      }
      if (hasAccess('assignments/assignment-tracking', userRole)) {
        availableGuides.push('assignments/assignment-tracking');
      }
      if (hasAccess('assignments/automatic-assignments', userRole)) {
        availableGuides.push('assignments/automatic-assignments');
      }
      
      // Member Portal guides
      if (hasAccess('member-portal/assignment-management', userRole)) {
        availableGuides.push('member-portal/assignment-management');
      }
      if (hasAccess('member-portal/dues-fees', userRole)) {
        availableGuides.push('member-portal/dues-fees');
      }
      if (hasAccess('member/automatic-dues', userRole)) {
        availableGuides.push('member/automatic-dues');
      }
    }

    const unseen = availableGuides.filter(guide => !preferences.seenGuides.includes(guide));
    setNewGuides(unseen);

    // Create notifications for new guides
    const newNotifications = unseen
      .filter(guide => !preferences.dismissedNotifications.includes(`new-guide-${guide}`))
      .map(guide => ({
        id: `new-guide-${guide}`,
        guideId: guide,
        title: getGuideTitle(guide),
        message: 'Click "View Guide" to learn more.'
      }));

    setNotifications(newNotifications);
  };

  const getGuideTitle = (guideId: string): string => {
    const titles: Record<string, string> = {
      // Dashboard guides
      'dashboard/dashboard-overview': 'Dashboard Overview Guide',
      'dashboard/chart-usage': 'Chart Usage and Interpretation',
      'dashboard/filter-functionality': 'Filter Functionality Guide',
      'dashboard/export-features': 'Export Features Guide',
      
      // Member guides
      'members/member-list-features': 'Member List Features and Filtering',
      'members/add-edit-members': 'Adding and Editing Members',
      'members/financial-information': 'Member Details and Financial Information',
      
      // Contribution guides
      'contributions/contribution-list': 'Contribution Management Overview',
      'contributions/add-contribution': 'Adding New Contributions',
      
      // Expense guides
      'expenses/expense-list': 'Expense Management Overview',
      'expenses/add-expense': 'Recording New Expenses',
      
      // Event guides
      'events/event-list': 'Event Management Overview',
      'events/add-event': 'Creating New Events',
      
      // Benefit guides
      'benefits/benefit-list': 'Benefit Management Overview',
      'benefits/benefit-form': 'Creating and Managing Benefits',
      
      // Town guides
      'towns/town-list': 'Town Management Overview',
      'towns/add-town': 'Adding New Towns',
      
      // Report guides
      'reports/report-center': 'Report Generation Overview',
      'reports/enhanced-financial': 'Enhanced Financial Reporting',
      
      // New component guides
      'sms/sms-configuration': 'SMS Center Configuration Guide',
      'email/email-templates': 'Email Center Templates Guide',
      'messages/messaging-system': 'Messaging System Guide',
      'audit/audit-trail': 'Audit Trail Viewer Guide',
      'approvals/financial-approvals': 'Financial Approval Queue Guide',
      'settings/system-settings': 'System Settings Guide',
      'admin/brand-settings': 'Brand Settings Guide',
      'stats/dashboard': 'Statistics Dashboard Guide',
      'reminders/reminder-management': 'Reminder Management Guide',
      
      // Assignment guides
      'assignments/assignment-management': 'Assignment Management Guide',
      'assignments/assignment-generation': 'Assignment Generation Guide',
      'assignments/assignment-tracking': 'Assignment Tracking Guide',
      'assignments/automatic-assignments': 'Automatic Assignments Guide',
      
      // Member Portal guides
      'member-portal/assignment-management': 'Member Assignment Management Guide',
      'member-portal/dues-fees': 'Member Dues and Fees Guide',
      
      // Member guides
      'member/automatic-dues': 'Understanding Automatic Dues Assignments'
    };
    return titles[guideId] || 'New Help Guide';
  };

  const dismissNotification = (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    updatePreferences({
      dismissedNotifications: [...preferences.dismissedNotifications, notificationId]
    });
  };

  const value = {
    isOpen,
    currentGuide,
    preferences,
    userRole,
    newGuides,
    notifications,
    openHelp,
    closeHelp,
    updatePreferences,
    hasAccess,
    markGuideAsSeen,
    dismissNotification,
    checkForNewGuides
  };

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
};